import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { LayoutService } from '../../layout/service/layout.service';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ENVIRONMENT } from '@env/environment';
import { View } from '../../layout/model/interfaces/view.interface';
import { NavigationTypes } from '@shared/model/enums/navigation-types.enum';
import { NavigationStateManager, NavigationSource, NavigationPhase } from '../../layout/service/navigation-state-manager.service';
import { UrlSyncService } from './url-sync.service';
import { getEnabledPages } from '@shared/utils/environment-utils';

@Injectable({ providedIn: 'root' })
export class RouteGuardService {
  private layoutService = inject(LayoutService);
  private router = inject(Router);
  private navStateManager = inject(NavigationStateManager);
  private location = inject(Location);
  private urlSyncService = inject(UrlSyncService);


  getRouteUrl(route: ActivatedRouteSnapshot): string {
    return '/' + (route.routeConfig?.path || '');
  }

  extractRouteSegment(url: string): string {
    try {
      const pathMatch = new URL(url).pathname.match(/\/app\/([^/]+)/);
      return pathMatch ? pathMatch[1] : '';
    } catch {
      return '';
    }
  }

  handleRoute = async (target: View, routeSnapshot?: ActivatedRouteSnapshot): Promise<boolean> => {
    const { route, iframeUrl } = target;
    const enabledPages = getEnabledPages();

    this.layoutService.isNavigationEvent = NavigationTypes.NAVIGATION_PANEL;

    // Check if route or its base path (for routes with params/sub-routes) is enabled
    // This handles:
    // - Exact matches: 'texts-tables' matches 'texts-tables'
    // - Sub-routes with slashes: 'users/uuid' matches 'users'
    // - Sub-routes with hyphens: 'texts-tables-editor/uuid' matches 'texts-tables'
    const isPageEnabled = enabledPages.some((page: string) => {
      // Exact match
      if (route === page) return true;
      // Check if route starts with page followed by '/' or '-'
      // This handles both 'users/uuid' and 'texts-tables-editor/uuid' cases
      return route.startsWith(page + '/') || route.startsWith(page + '-');

    });

    this.layoutService.showOldApp = !isPageEnabled;

    // TODO: remove once transition to Angular 19 done ---
    if (this.layoutService.showOldApp && iframeUrl != '') {
      const normalizedRoute = route.startsWith('/') ? route : `/${route}`;

      // Check if browser URL was changed by location.go() and differs from requested route
      // CRITICAL: If current navigation was initiated by IFRAME, verify iframe URL matches
      // If it doesn't match, we still need to reload the iframe
      const currentState = this.navStateManager.currentState;

      // Normalize both URLs for comparison (handle workflows2 vs workflow2, strip query params)
      const normalizeForComparison = (url: string | null): string => {
        if (!url) return '';
        // Strip query parameters and hash
        const pathOnly = url.split('?')[0].split('#')[0];
        return pathOnly.replace(/^\/workflows2(\/|$)/, '/workflow2$1').replace(/\/$/, '');
      };

      const normalizedCurrentTarget = normalizeForComparison(currentState.targetUrl);
      const normalizedRequestedRoute = normalizeForComparison(normalizedRoute);

      // If browser URL was changed by location.go() and user is now navigating to a different route,
      // skip the iframe check and force reload
      if (currentState.source === NavigationSource.IFRAME && normalizedCurrentTarget === normalizedRequestedRoute) {
        // CRITICAL: Double-check if the iframe ACTUALLY shows the correct content
        // If not, we need to reload it despite iframe initiating the navigation
        const currentIframe = document.getElementById('oldAppIframe') as HTMLIFrameElement;
        let shouldReloadIframe = false;

        if (currentIframe?.contentWindow?.location?.href) {
          try {
            const actualIframeUrl = currentIframe.contentWindow.location.href;
            const actualPath = new URL(actualIframeUrl).pathname;

            // Build expected path
            let expectedPath = `/app/${iframeUrl}`;
            if (expectedPath.includes('?')) {
              expectedPath = expectedPath.split('?')[0];
            }

            // If iframe URL doesn't match what we expect, we MUST reload it
            if (actualPath !== expectedPath && actualPath !== expectedPath.replace(/_/g, '-')) {
              shouldReloadIframe = true;
            }
          } catch (_error) {
            shouldReloadIframe = true;
          }
        } else {
          shouldReloadIframe = true;
        }

        // If iframe shows correct content, skip reload
        if (!shouldReloadIframe) {
          // CRITICAL: Build the full URL from the route snapshot (including query params)
          // We can't use this.router.url because navigation is being cancelled, so router hasn't updated yet
          let fullRouterUrl = normalizedRoute;
          if (routeSnapshot) {
            // Build query string from route snapshot
            const queryParams = routeSnapshot.queryParams;
            if (Object.keys(queryParams).length > 0) {
              const queryString = Object.keys(queryParams)
                .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
                .join('&');
              fullRouterUrl = `${normalizedRoute}?${queryString}`;
            }
          }
          this.layoutService.lastSyncedUrl = fullRouterUrl;

          // Update browser URL without triggering navigation
          setTimeout(() => {
            this.location.go(fullRouterUrl);
            // Emit event to notify sidebar of URL change
            this.urlSyncService.urlChanged.emit(fullRouterUrl);
          }, 0);

          // Return FALSE to prevent component destruction and flickering
          // Note: Styles will be applied by iframe.component.ts after detecting navigation failure
          return false;
        }
        // Otherwise, fall through to normal reload logic
      }

      // Check if navigation should be allowed
      const shouldAllow = this.navStateManager.shouldAllowNavigation(normalizedRoute, NavigationSource.TOP_WINDOW);
      if (!shouldAllow) {
        // CRITICAL FIX: Even if navigation is blocked, check if iframe needs update
        // This handles the case where rapid clicking causes navigation to be blocked
        // but the iframe is showing wrong content
        const currentIframe = document.getElementById('oldAppIframe') as HTMLIFrameElement;
        if (currentIframe?.contentWindow?.location?.href) {
          const currentIframeUrl = currentIframe.contentWindow.location.href;
          let expectedUrl = `${ENVIRONMENT.APP_URL}${iframeUrl}`;
          if (iframeUrl.includes('?')) {
            expectedUrl += '&app=old';
          } else {
            expectedUrl += '?app=old';
          }

          // Compare URLs (without query params for simplicity)
          const currentPath = new URL(currentIframeUrl).pathname;
          const expectedPath = new URL(expectedUrl, window.location.origin).pathname;

          if (currentPath !== expectedPath) {
            // Force update iframe
            this.layoutService.isVisible = false;
            this.layoutService.loadUrl(expectedUrl);

            // Don't return - let the normal flow continue to update state properly
            // But we've already forced the iframe to load correct content
          } else {
            // Don't update layoutService URL when navigation is blocked
            // The iframe is already showing correct content, so no action needed
            // Cancel navigation since iframe is already correct and lock is held
            return false;
          }
        } else {
          // Can't access iframe and navigation is blocked - don't update URL
          // Cancel navigation since we're blocked
          return false;
        }
      }

      // Initiate navigation with state manager
      const lockId = shouldAllow ? this.navStateManager.initiateTopWindowNavigation(normalizedRoute) : null;
      if (!lockId && shouldAllow) {
        // Don't call loadUrl() when lock fails - navigation is rejected
        // Updating the URL would cause component recreation to use wrong URL
        // Cancel navigation since we couldn't acquire lock
        return false;
      }

      // Update legacy state
      this.layoutService.lastSyncedUrl = normalizedRoute;
      this.layoutService.navigationInitiatedByTopWindow = normalizedRoute;

      // Build iframe URL
      let finalUrl = `${ENVIRONMENT.APP_URL}${iframeUrl}`;
      if (iframeUrl.includes('?')) {
        finalUrl += '&app=old';
      } else {
        finalUrl += '?app=old';
      }

      // Mark as loading phase only if we have a lock
      if (lockId) {
        this.navStateManager.updatePhase(NavigationPhase.LOADING);
      }

      // Hide visibility BEFORE loading new URL to prevent flicker
      // Only do this if we haven't already done it above
      if (shouldAllow) {
        this.layoutService.isVisible = false;
      }

      // Load new URL (only if we haven't already forced it above)
      if (shouldAllow) {
        this.layoutService.loadUrl(finalUrl);
      }

      // The iframe load listener will handle styles application and visibility
      // Note: We removed the manual setTimeout calls here as they're now handled
      // by the improved iframe load listener with proper state management

      // Bind select changes after a reasonable delay
      setTimeout(() => {
        this.layoutService.bindSelectChange();
      }, 1000);

      // Allow navigation to proceed
      return true;
    }

    // If page is enabled (not using iframe), reset navigation state and allow navigation
    // CRITICAL: Reset NavStateManager when navigating to native Angular pages
    // This clears the lastSyncedUrl so that future navigations to iframe pages won't be blocked
    // Example: /templates (iframe) -> /categories (native) -> /templates (iframe)
    // Without this reset, the second navigation to /templates would be blocked because
    // lastSyncedUrl === '/templates' from the first navigation
    this.navStateManager.reset();
    return true;
  };
}
